import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Button,
    Spacer,
    Text,
} from '@zillow/constellation';

import ModalActions from 'app/shared/actions/ModalActions';

import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';

import {
    StyledCTAContainer,
    StyledList,
    StyledPaymentOptionContainer,
    StyledPaymentOptionItem,
    StyledPaymentOptionsModal,
} from './StyledComponents';

const propTypes = {
    onOptionSelected: PropTypes.func.isRequired,
};

const defaultProps = {};

const CONDITIONAL_REASON = 'Eligible for Lead Guarantee';

const OPTION_CONFIG = {
    [PaidInclusionConstants.PRODUCT_TYPE[1]]: {
        title: 'Pay a weekly fee',
        titleReason: 'Best for fast-moving properties',
        reasonList: [
            'Pay only for the time you need your listing active on Zillow',
            `${CONDITIONAL_REASON}`,
            'Deactivate anytime.  Your listing auto-renews until canceled.',
        ],
    },
    [PaidInclusionConstants.PRODUCT_TYPE[2]]: {
        title: 'Pay a flat fee',
        titleReason: 'Best for longer postings',
        reasonList: [
            'Pay once for more time to find your perfect renter',
            'Deactivate when you find a tenant',
            'Your listing will automatically expire after 90 days',
        ],
    },
};

const PaymentOptionsModal = (props) => {
    const { onOptionSelected } = props;

    const dispatch = useDispatch();

    const { currentProperty } = useSelector(state => state.properties);
    const { paidInclusion: { products: productList } } = currentProperty;

    const handleOptionSelected = (type) => {
        onOptionSelected(type);
        dispatch(ModalActions.hideModal());
    };

    const renderPaymentOptionItem = ({
        paymentValidDuration,
        isEligibleForLeadGuarantee,
        price,
        type,
    }) => {
        const {
            title,
            titleReason,
            reasonList,
        } = OPTION_CONFIG[type];


        const periodDisplay = type === PaidInclusionConstants.PRODUCT_TYPE[1]
            ? `per ${paymentValidDuration}`
            : `for ${paymentValidDuration}`;

        const btnTitle = `Publish for ${price}`;

        return (
            <StyledPaymentOptionItem
                key={type}
                className="PaymentOption-Item"
                paddingTop="lg"
                paddingX="md"
                paddingBottom="sm"
            >
                <Spacer marginBottom="md">
                    <Text
                        as="div"
                        className="Option-Title"
                        fontType="h5"
                    >
                        {title}
                    </Text>
                </Spacer>

                <Spacer
                    className="Option-Period"
                    marginBottom="xs"
                >
                    <Text
                        as="span"
                        fontType="h3"
                    >
                        {price}
                    </Text>
                    <span>
                        &nbsp;
                        {periodDisplay}
                    </span>
                </Spacer>

                <Spacer marginBottom="md">
                    <Text
                        as="div"
                        className="Option-Reason-Summary"
                        fontType="h6"
                    >
                        {titleReason}
                    </Text>
                </Spacer>

                <StyledList className="Option-ReasonList" type={type} data-test="Option-ReasonList">
                    {reasonList.map((reason) => {
                        if (reason === CONDITIONAL_REASON) {
                            if (type === PaidInclusionConstants.PRODUCT_TYPE[1]
                                    && isEligibleForLeadGuarantee === false) {
                                return null;
                            }
                        }

                        return (
                            <StyledList.Item
                                key={reason}
                                className="ReasonList-Item"
                                data-test="ReasonList-Item"
                            >
                                {reason}
                            </StyledList.Item>
                        );
                    })}
                </StyledList>

                <StyledCTAContainer className="CTA-Container">
                    <Button
                        buttonType="secondary"
                        fluid={true}
                        onClick={() => handleOptionSelected(type)}
                    >
                        { btnTitle }
                    </Button>
                </StyledCTAContainer>
            </StyledPaymentOptionItem>
        );
    };

    return (
        <StyledPaymentOptionsModal className="PaymentOptionsModal">
            <Spacer className="FreeListingsAvailable-Container" marginBottom="md">
                <Text as="span" fontType="bodyHeading">0</Text>
                <Text as="span" fontType="body">
                    &nbsp;free listings available
                </Text>
            </Spacer>

            {productList && productList.length > 0 ? (
                <StyledPaymentOptionContainer className="PaymentOption-Container">
                    {productList.map(product => renderPaymentOptionItem(product))}
                </StyledPaymentOptionContainer>
            ) : (
                <StyledPaymentOptionContainer>
                    <Text>No payment options to display</Text>
                </StyledPaymentOptionContainer>
            )}
        </StyledPaymentOptionsModal>
    );
};

PaymentOptionsModal.propTypes = propTypes;
PaymentOptionsModal.defaultProps = defaultProps;

export { CONDITIONAL_REASON };
export default PaymentOptionsModal;
